import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useMutation } from 'urql';

import { Alert } from 'components/alert';
import { Button } from 'components/button';
import { Input } from 'components/input';
import colors from 'constants/styles/colors';
import media from 'constants/styles/media';
import { ContactFormValues } from '../types';
import { useAlert } from '../utils';
import { useFooterContext } from 'utils/footer-context';
import { useTranslation } from 'gatsby-plugin-react-i18next';

const SForm = styled.form`
    margin-top: 36px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: relative;
    width: 100%;
`;

const STopInputWrapper = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: ${media.tablet}) {
        grid-template-columns: 1fr;
    }
`;

const SField = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SFieldLabel = styled.label`
    font-weight: 500;
    font-size: 14px;
    line-height: 20px
    letter-spacing: 0.02em;
    color: ${colors.gray_70};
`;

const STextArea = styled.textarea`
    width: 100%;
    height: 116px;
    background: #ffffff;
    border: 1px solid ${colors.gray_40};
    border-radius: 8px;
    resize: none;
    padding: 16px 20px;
    text-align: left;
    outline: none;
    font-size: 16px;
    line-height: 1.25;
    color: ${colors.gray_90};
    margin-bottom: 16px;

    ::placeholder,
    ::-webkit-input-placeholder {
        font-size: 16px;
        font-weight: 400;
        letter-spacing: 0.01em;
        color: ${colors.text};
    }

    @media (max-width: ${media.tablet}) {
        width: 100%;
    }
`;

const SConsentRow = styled.label`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 74px;
    color: ${colors.gray_70};
    font-size: 16px;
    line-height: 24px;
    user-select: none;
    @media (max-width: ${media.tablet}) {
        margin-bottom: 24px;
    }
`;

const SCheckbox = styled.input`
    width: 20px;
    height: 20px;
    border-radius: 8px;
    accent-color: ${colors.orange};
`;

const SButton = styled(Button)`
    width: 340px;

    @media (max-width: ${media.tablet}) {
        width: 100%;
    }
`;

export const Form = () => {
    const { t } = useTranslation();
    const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
    const { register, handleSubmit, setValue } = useForm<ContactFormValues & { consent: boolean }>({
        defaultValues: {
            consent: false,
        },
    });
    const [, update] = useMutation(SEND_CONTACT_EMAIL_MUTATION);
    const { messageStatus, successAlert, errorAlert, hideAlert } = useAlert();
    const { textAreaText } = useFooterContext();

    useEffect(() => {
        setValue('message', textAreaText);
    }, [textAreaText, setValue]);

    const onInvalid = () => {
        setAlertMessage(t('contact.form.validationError'));
        errorAlert();
        hideAlert(3000);
    };

    const onSubmit = async (values: ContactFormValues & { consent: boolean }) => {
        try {
            setAlertMessage(undefined);
            const { name, email, message } = values;
            const { data } = await update({ input: { name, email, message } });

            data.sendContactEmail.success ? successAlert() : errorAlert();
        } catch (err) {
            errorAlert();
        } finally {
            hideAlert(3000);
        }
    };

    return (
        <SForm onSubmit={handleSubmit(onSubmit, onInvalid)}>
            {messageStatus.sent && <Alert error={messageStatus.error} message={alertMessage} />}
            <STopInputWrapper>
                <Input<ContactFormValues & { consent: boolean }>
                    name="name"
                    label={t('contact.form.name.label')}
                    placeholder={t('contact.form.name.placeholder')}
                    type="text"
                    register={register}
                    required
                />
                <Input<ContactFormValues & { consent: boolean }>
                    name="email"
                    label={t('contact.form.email.label')}
                    placeholder={t('contact.form.email.placeholder')}
                    type="email"
                    register={register}
                    required
                />
            </STopInputWrapper>
            <SField>
                <SFieldLabel htmlFor="message">{t('contact.form.message.label')}</SFieldLabel>
                <STextArea
                    id="message"
                    placeholder={t('contact.form.message.placeholder')}
                    cols={30}
                    rows={10}
                    {...register('message', { required: true })}
                />
            </SField>
            <SConsentRow>
                <SCheckbox type="checkbox" {...register('consent', { required: true })} />
                {t('contact.form.consent')}
            </SConsentRow>
            <SButton title={t('contact.form.submit')} />
        </SForm>
    );
};

const SEND_CONTACT_EMAIL_MUTATION = `mutation SendContactEmailMutation($input: SendContactEmailInput!) {
    sendContactEmail(input: $input) {
        success
    }
}`;
